/**
 * MFA Enforcement Section (Admin)
 * Toggle MFA enforcement per role
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { AdminPanelSettings } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { authService } from '../../services/authService';
import { MfaEnforcedRole } from '../../types/auth';

const MfaEnforcementSection: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [roles, setRoles] = useState<MfaEnforcedRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<number | null>(null);

  const loadRoles = useCallback(async () => {
    try {
      const data = await authService.getEnforcedRoles();
      setRoles(data);
    } catch {
      enqueueSnackbar('Erro ao carregar roles', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleToggle = async (roleId: number, enforce: boolean) => {
    setUpdating(roleId);
    try {
      await authService.enforceMfaForRole(roleId, enforce);
      setRoles((prev) =>
        prev.map((r) => (r.id === roleId ? { ...r, mfaEnforced: enforce } : r))
      );
      enqueueSnackbar(
        enforce
          ? 'MFA agora é obrigatório para esta role'
          : 'MFA não é mais obrigatório para esta role',
        { variant: 'success' }
      );
    } catch {
      enqueueSnackbar('Erro ao atualizar enforcement', { variant: 'error' });
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AdminPanelSettings color="primary" />
          Exigir MFA por Perfil
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Quando ativado, todos os usuários com a role selecionada serão obrigados a configurar MFA
          e não poderão desabilitá-lo.
        </Alert>
        <List>
          {roles.map((role) => (
            <ListItem key={role.id} divider>
              <ListItemText
                primary={role.displayName}
                secondary={role.name}
              />
              <ListItemSecondaryAction>
                {updating === role.id ? (
                  <CircularProgress size={24} />
                ) : (
                  <Switch
                    checked={role.mfaEnforced}
                    onChange={(e) => handleToggle(role.id, e.target.checked)}
                    color="primary"
                  />
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default MfaEnforcementSection;
